import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext();
export function useAuth() {
  return useContext(AuthContext);
}

export const MyContext = createContext();
export function useAppContext() {
  return useContext(MyContext);
}

const AUTH_API_BASE_URL =
  (typeof process !== 'undefined' && process.env.REACT_APP_AUTH_API_BASE_URL) ||
  'https://stark-rwae-app-awc3egftahdtgwca.centralus-01.azurewebsites.net';

const LOCAL_STORAGE_USER_KEY = 'srwae_auth_user';

const LOCAL_LOGIN_WHITELIST = [];

const getPublicUser = (user) => {
  if (!user) return null;
  const { password, ...publicUser } = user;
  return publicUser;
};

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const readStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeUser = (user) => {
  try {
    if (user) {
      window.localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
};

export const AppContext = ({ children }) => {
  const [user, setUser] = useState(() => readStoredUser());
  const [authLoading, setAuthLoading] = useState(true);
  const [subscription, setSubscription] = useState({
    hasSubscription: false,
    nextBillingDate: null,
    isTrialActive: false,
    trialEndDate: null
  });

  const [App_Data, setApp_Data] = useState({});

  const setAuthenticatedUser = useCallback((nextUser) => {
    setUser(nextUser);
    storeUser(nextUser);
  }, []);

const refreshSession = useCallback(async () => {

  // ----------------------------------------------------
  // 1. Check Flask/local username-password authentication
  // ----------------------------------------------------
  try {
    const response = await fetch(
      `${AUTH_API_BASE_URL}/api/auth/session`,
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store'
      }
    );

    if (response.ok) {
      const payload = await safeJson(response);

      if (payload?.authenticated && payload?.user) {
        setAuthenticatedUser(payload.user);
        return payload.user;
      }
    }
  } catch (error) {
    console.warn('Local session check failed:', error);
  }


  // ----------------------------------------------------
  // 2. Check Azure Easy Auth / Google
  // ----------------------------------------------------
  try {
    const response = await fetch('/.auth/me', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });

    if (!response.ok) {
      setAuthenticatedUser(null);
      return null;
    }

    const identities = await response.json();

    if (!Array.isArray(identities) || identities.length === 0) {
      setAuthenticatedUser(null);
      return null;
    }

    const identity = identities[0];
    const claims = identity.user_claims || [];

    const getClaim = (...names) => {
      const normalizedNames = names.map((name) =>
        name.toLowerCase()
      );

      const claim = claims.find((item) => {
        const type = String(item?.typ || '').toLowerCase();

        return normalizedNames.some(
          (name) =>
            type === name ||
            type.endsWith(`/${name}`)
        );
      });

      return claim?.val || null;
    };

    const email =
      identity.user_name ||
      getClaim(
        'email',
        'emailaddress',
        'preferred_username'
      );

    const googleUser = {
      id:
        identity.user_id ||
        getClaim('sub', 'nameidentifier'),

      username: email,
      email,

      displayName:
        getClaim('name') ||
        email ||
        'Google User',

      provider: 'google',
      authProvider:
        identity.provider_name || 'google',

      roles: []
    };

    setAuthenticatedUser(googleUser);

    return googleUser;

  } catch (error) {
    console.warn(
      'Azure Easy Auth session check failed:',
      error
    );

    setAuthenticatedUser(null);
    return null;
  }

}, [setAuthenticatedUser]);




  useEffect(() => {
    let isMounted = true;

    refreshSession()
      .catch(() => {
        if (isMounted) {
          setAuthenticatedUser(readStoredUser());
        }
      })
      .finally(() => {
        if (isMounted) setAuthLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [refreshSession, setAuthenticatedUser]);

  const login = useCallback(
    async (usernameOrEmail, password) => {
      const normalizedUsername = usernameOrEmail.trim().toLowerCase();

      try {
        const response = await fetch(`${AUTH_API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: usernameOrEmail.trim(), password })
        });

        const payload = await safeJson(response);

        if (response.ok && payload?.authenticated && payload?.user) {
          setAuthenticatedUser(payload.user);
          return payload.user;
        }
      } catch {
        // If the backend is not running during local frontend work, use the local whitelist below.
      }

      const matchedUser = LOCAL_LOGIN_WHITELIST.find((candidate) => {
        const candidateUsername = (candidate.username || '').toLowerCase();
        const candidateEmail = (candidate.email || '').toLowerCase();
        return (
          candidate.password === password &&
          (candidateUsername === normalizedUsername || candidateEmail === normalizedUsername)
        );
      });

      if (!matchedUser) {
        throw new Error('Login failed. Please check your username and password.');
      }

      const publicUser = getPublicUser(matchedUser);
      setAuthenticatedUser(publicUser);
      return publicUser;
    },
    [setAuthenticatedUser]
  );

const logout = useCallback(async () => {
  const currentUser = user;

  setSubscription({
    hasSubscription: false,
    nextBillingDate: null,
    isTrialActive: false,
    trialEndDate: null
  });

  // Local username/password account
  if (currentUser?.provider === 'local') {
    try {
      await fetch(`${AUTH_API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Local logout failed:', error);
    }

    setAuthenticatedUser(null);
    window.location.assign('/login_logout');
    return;
  }

  // Google / Azure Easy Auth account
  setAuthenticatedUser(null);

  window.location.assign(
    '/.auth/logout?post_logout_redirect_uri=' +
    encodeURIComponent('/login_logout')
  );
}, [user, setAuthenticatedUser]);

  const resetPassword = useCallback(async (email) => {
    const response = await fetch(`${AUTH_API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const payload = await safeJson(response);
      throw new Error(payload?.error || 'Password reset failed. Please try again.');
    }

    return safeJson(response);
  }, []);

const startSsoLogin = useCallback((provider) => {
  const normalizedProvider = String(provider || '').toLowerCase();

  const azureProviders = {
    google: 'google',
    microsoft: 'aad',
    apple: 'apple'
  };

  const azureProvider = azureProviders[normalizedProvider];

  if (!azureProvider) {
    throw new Error('Unsupported authentication provider.');
  }

  const params = new URLSearchParams();

  params.set(
    'post_login_redirect_uri',
    '/login_logout?auth=success'
  );

  // همیشه صفحه انتخاب حساب Google نمایش داده شود
  if (normalizedProvider === 'google') {
    params.set('prompt', 'select_account');
  }

  window.location.assign(
    `/.auth/login/${azureProvider}?${params.toString()}`
  );
}, []);

  const contextValue = useMemo(
    () => ({
      isLoggedIn: Boolean(user),
      authLoading,
      user,
      login,
      logout,
      resetPassword,
      startSsoLogin,
      refreshSession,
      subscription,
      setSubscription
    }),
    [
      authLoading,
      user,
      login,
      logout,
      resetPassword,
      startSsoLogin,
      refreshSession,
      subscription,
      setSubscription
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <MyContext.Provider value={{ App_Data, setApp_Data }}>{children}</MyContext.Provider>
    </AuthContext.Provider>
  );
};
